import AddTool, { AddToolContext } from './add-tool';
import DrawerView from '../../../views/drawer-view';
import SectionView from '../../../views/section-view';
import Signal from '../../../control-flow/signal';
import logging from '../../../logging';
import visibleSize from '../../../view3d/visible-size';
import { Box3 } from 'three';
import { Drawer, Group, Handle, Section } from '../../../domain';
import { PI } from '../../../math/constants';
import { getParentSectionView } from '../../../controllers/section-controllers';
import { isBoxOnly } from '../../../controllers/drawer-controllers';
import { overlapsSectionContent, overlapsSectionDoor } from '../../../view3d/overlaps-section-view';
import { round2slot } from '../../../measurement';

const log = logging('tools');


export class AddDrawerTool extends AddTool
{

    /**
     * This is the section that the mouse is currently over.
     */
    target?: SectionView;

    model: Drawer;
    proxy: DrawerView;

    declare onSuccess: Signal<[Drawer]>;

    constructor(
        drawer: Drawer, elt: HTMLElement, ctx: AddToolContext) 
    {
        super(elt, ctx);

        this.model = Drawer.fromJSON({
            width: 24,
            height: drawer.height,
            depth: 14,
            scoop: Boolean(drawer.scoop),
        });

        this.model.frame = drawer.frame?.clone(true, this.model);
        this.model.panel = drawer.panel?.clone(true, this.model);

        this.ctx.defaults.reset(this.model);

        if(drawer.box.modelAssetId) {
            this.model.box.modelAssetId = drawer.box.modelAssetId;
            this.ctx.defaults.reset(this.model.box);

            if(drawer.panel == null) {
                this.model.handles = [];
            } else {
                this.model.handles = [Handle.fromJSON({}, this.model)];
            }
        }

        if(!isBoxOnly(this.model) && !drawer.box.modelAssetId) {
            this.model.handles = [Handle.fromJSON({}, this.model)];
        }

        this.model.handles.forEach(this.ctx.defaults.reset);

        if(isBoxOnly(this.model)) {
            this.model.box.width = this.model.width;
            this.model.box.height = this.model.height;
            this.model.box.depth = this.model.depth - 0.75;
        } else {
            this.model.box.width = this.model.width - 1;
            this.model.box.height = this.model.height - 1;
            this.model.box.depth = this.model.depth;
        }

        if(drawer.height < 6) {
            this.model.frame = null;
        }

        const group = Group.fromJSON({
            rotation: { x: PI/10, y: PI/20, z: 0 },
        });
        group.drawers.push(this.model);
        group.commit(true);

        this.updateThumbnail(group);
    }

    dragenter(): void {
        log.debug('AddDrawerTool.dragenter()');

        this.proxy = new DrawerView(this.model);
    }

    dragover(event: PointerEvent, target: HTMLElement): void {
        log.debug('AddDrawerTool.dragover()');

        this.updateWorldPosition(event, target);
        this.onDisplayChange.trigger();

        const
            y = round2slot(this.worldPosition.y),
            hit = this.ctx.picker.topHit;

        let
            section: Section,
            sectionView: SectionView;
        if(hit instanceof SectionView) {
            section = hit.model;
            sectionView = hit;
        } else if(hit) {
            try {
                sectionView = getParentSectionView(hit) as SectionView;
                section = sectionView.model;
            } catch(error) {
                log.error(error);
            }
        }

        const box = new Box3();
        visibleSize(this.proxy, box);
        const height = box.max.y - box.min.y;

        if(sectionView
           && (isBoxOnly(this.model) || !overlapsSectionDoor(sectionView, y, height, this.proxy))
           && !overlapsSectionContent(sectionView, box.min.y, height, this.proxy))
        {
            const target: SectionView = this.target = sectionView;

            if(this.proxy) target.add(this.proxy);
            this.model.position.x = -0.25;
            this.model.position.y = y;
            this.model.position.z = target.model.depth;
            // We change the box width first since the drawer view only binds
            // the model width
            this.model.box.width = target.model.width - 1;
            this.model.width = target.model.width + 0.5;
            this.model.depth = target.model.depth;
            this.model.box.depth = target.model.depth;
            if(this.model.handles.length > 0)
                this.model.handles[0].position.x = target.model.width / 2;
        }
        else {
            delete this.target;

            // If we're still waiting for the proxy to be made, then do nothing
            // else.
            if(!this.proxy) return;

            // check if the proxy belongs to the scene ... if so, fine, else
            // remake the proxy and add it back to the scene.
            if(this.proxy.parent != this.ctx.scene) {
                this.proxy.removeFromParent();
                this.ctx.scene.add(this.proxy);
            }

            // regardless, set the position of the proxy
            this.model.position.x = this.worldPosition.x - 6;
            this.model.position.y = this.worldPosition.y,
            this.model.position.z = 25;
            this.model.width = 24;
            this.model.box.width = 16;
            this.model.depth = 14
            this.model.box.depth = 14;
            if(this.model.handles.length > 0)
                this.model.handles[0].position.x = 9;
        }
    }

    dragleave(): void {
        log.debug('AddDrawerTool.dragleave()');
        this.proxy?.removeFromParent();
        this.onDisplayChange.trigger();
    }

    cancel(): void {
        log.debug('AddDrawerTool.cancel()');
        this.proxy?.removeFromParent();
        this.onDisplayChange.trigger();
    }

    end(): void {
        log.debug('AddDrawerTool.end()');
        this.proxy?.removeFromParent();
        this.onDisplayChange.trigger();

        // if we ended over a section, then we'll have a defined target
        // property, which means we can drop something.
        if(this.target) {
            this.model.commit();
            const model = this.model.clone(true);
            model.parent = this.target.model;
            this.target.model.drawers.push(model);
            this.onSuccess.trigger(model);
        }
    }

    isBoxOnly() {
        return this.model.scoop || this.model.panel == null || this.model.panel.style == null;
    }
}
export default AddDrawerTool;
